import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../config/firebaseConfig';
import { UserAuth } from '../../context/authContext';
import LeaderboardStage from './LeaderboardStage';
import PlayingStage from './PlayingStage';
import RoleAssignmentDisplay from './RoleAssignmentDisplay';
import StakingStage from './StakingStage';
import WaitingStage from './WaitingStage';

// Constants
const TARGET_BLOCKS = 5;
const LEADERBOARD_DURATION = 60; // seconds
const WINNER_DISPLAY_DURATION = 10; // seconds

const GameTab = () => {
  const [roomId, setRoomId] = useState(localStorage.getItem('roomId') || '');
  const [roomData, setRoomData] = useState(null);
  const [role, setRole] = useState(localStorage.getItem('role') || '');
  const { userData } = UserAuth();
  const [loading, setLoading] = useState(true);

  // Listen to room document in real-time
  useEffect(() => {
    if (!roomId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const roomRef = doc(db, 'rooms', roomId);
    const unsubscribe = onSnapshot(roomRef, async (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setRoomData(data);

        // Update role if it changed
        if (data.miners && data.miners.includes(userData.publicKey)) {
          localStorage.setItem('role', 'validator');
          setRole('validator');
        } else if (
          data.participants &&
          data.participants.includes(userData.publicKey)
        ) {
          localStorage.setItem('role', 'participant');
          setRole('participant');
        }

        // Check for block count transition (PLAYING → LEADERBOARD)
        if (
          data.roomType === 'ASTRAPOS' &&
          data.gameState?.stage === 'PLAYING' &&
          data.gameState?.blocksMinedThisCycle >= TARGET_BLOCKS
        ) {
          console.log('Block target reached! Transitioning to leaderboard...', {
            roomType: data.roomType,
            stage: data.gameState?.stage,
            blocksMined: data.gameState?.blocksMinedThisCycle,
          });
          try {
            // Attempt to transition to leaderboard (only one client will succeed)
            const { transitionToLeaderboard } = await import(
              '../../utils/astrapos/stageTransitions'
            );
            await transitionToLeaderboard(roomId);
          } catch (error) {
            console.log(
              'Leaderboard transition already triggered or error:',
              error
            );
          }
        }

        // Monitor for stage expiry transitions
        if (data.roomType === 'ASTRAPOS' && data.gameState?.stageEndsAt) {
          const now = Date.now();
          const endsAt = data.gameState.stageEndsAt.toDate
            ? data.gameState.stageEndsAt.toDate().getTime()
            : data.gameState.stageEndsAt.getTime();

          if (now >= endsAt) {
            // Stage has expired, handle transition based on current stage
            const { performStageTransition } = await import(
              '../../utils/astrapos/stageTransitions'
            );

            try {
              switch (data.gameState.stage) {
                case 'STAKING':
                  // Staking ended, transition to winner display
                  // This should be handled by stakingExpiry in StakingStage
                  break;
                case 'WINNER_DISPLAY':
                  await performStageTransition(roomId, 'PLAYING');
                  break;
                case 'LEADERBOARD':
                  await performStageTransition(roomId, 'WAITING');
                  break;
                default:
                  break;
              }
            } catch (error) {
              console.log('Stage transition already triggered');
            }
          }
        }
      } else {
        // Room deleted or doesn't exist
        localStorage.removeItem('roomId');
        localStorage.removeItem('role');
        window.location.href = '/';
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [roomId, userData.publicKey]);

  if (loading) {
    return (
      <div className="min-h-screen pt-10 pb-8 px-6 flex items-center justify-center">
        <div className="text-white text-center">
          <p className="text-lg">Loading game...</p>
        </div>
      </div>
    );
  }

  if (!roomData) {
    return (
      <div className="min-h-screen pt-10 pb-8 px-6 flex items-center justify-center">
        <div className="text-white text-center">
          <p className="text-lg">Room not found</p>
        </div>
      </div>
    );
  }

  const currentStage = roomData.gameState?.stage || 'WAITING';

  // Render based on current game stage
  const renderStage = () => {
    switch (currentStage) {
      case 'WAITING':
        return (
          <WaitingStage
            roomId={roomId}
            roomData={roomData}
            userData={userData}
            role={role}
          />
        );
      case 'STAKING':
        return (
          <StakingStage
            roomId={roomId}
            roomData={roomData}
            userData={userData}
            role={role}
          />
        );
      case 'WINNER_DISPLAY':
        return (
          <RoleAssignmentDisplay
            roomId={roomId}
            roomData={roomData}
            userData={userData}
            role={role}
            winnerDisplayDuration={WINNER_DISPLAY_DURATION}
          />
        );
      case 'PLAYING':
        return (
          <PlayingStage
            roomId={roomId}
            roomData={roomData}
            userData={userData}
            role={role}
            targetBlocks={TARGET_BLOCKS}
          />
        );
      case 'LEADERBOARD':
        return (
          <LeaderboardStage
            roomId={roomId}
            roomData={roomData}
            leaderboardDuration={LEADERBOARD_DURATION}
          />
        );
      default:
        return (
          <div className="min-h-screen pt-10 pb-8 px-6 flex items-center justify-center">
            <div className="text-white text-center">
              <p className="text-lg">Unknown stage: {currentStage}</p>
            </div>
          </div>
        );
    }
  };

  const copyRoomId = () => {
    navigator.clipboard.writeText(roomId);
    const event = new CustomEvent('roomIdCopied');
    window.dispatchEvent(event);
  };

  return (
    <div className="min-h-screen pt-10 pb-8 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Room Info Header */}
        <div className="mb-8 bg-gradient-to-r from-purple-500/10 to-blue-500/10 backdrop-blur rounded-xl p-6 border border-purple-500/20">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-white text-sm font-medium mb-1">Room ID</h2>
              <p className="text-white/60 text-xs">
                Share this ID with other players to join
              </p>
            </div>
            <div className="flex items-center gap-2">
              <code className="bg-black/30 text-purple-300 px-4 py-2 rounded-lg font-mono text-sm border border-purple-500/20">
                {roomId}
              </code>
              <button
                onClick={copyRoomId}
                className="px-4 py-2 bg-purple-500/30 hover:bg-purple-500/50 text-purple-200 rounded-lg font-medium transition-colors text-sm flex items-center gap-2"
                title="Copy room ID"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                  <rect x="9" y="2" width="6" height="4" rx="1" ry="1" />
                </svg>
                Copy
              </button>
            </div>
          </div>
        </div>

        {renderStage()}
      </div>
    </div>
  );
};

export default GameTab;
